import clsx from 'clsx'
import {UAParser} from 'ua-parser-js'

import CallIcon from '@/assets/icons/call.svg'
import shopJpg from '@/assets/images/backgrounds/shop.jpg?minW=1024&picture-image-tools'
import Phone from '@/components/Phone/Phone'
import Picture from '@/components/Picture'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {type SearchInputControl} from '@/layout/SearchInputWithAutocomplete'
import {useLocale} from '@/lib/locale/locale'
import {getPhoneInfoByLocale} from '@/lib/locale/phoneNumbers'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import isFunction from '@/utils/types/guards/isFunction'

import style from './HomePageDefaultContent.module.scss'
const SearchInputWithAutocomplete = lazy(
  async () => await import('@/layout/SearchInputWithAutocomplete'),
)

interface Props {
  searchInputRef?: Ref<SearchInputControl | null>
}

export default memo<Props>(function HomePageDefaultContent({searchInputRef}) {
  const searchWrapperRef = useRef<HTMLDivElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const backgroundImageRef = useRef<HTMLImageElement>(null)
  const [locale] = useLocale()
  const [showPhoneDialog, setShowPhoneDialog] = useState(false)
  const [shouldFocusTrigger, setShouldFocusTrigger] = useState(false)
  const phoneInfo = getPhoneInfoByLocale(locale)

  const parser = new UAParser()
  const result = parser.getResult()
  const isMobile = result.device.type === 'mobile'

  const searchInputInternalRef = useRef<SearchInputControl>(null)

  const handleSearchSubmit = useCallback(() => {
    searchInputInternalRef.current?.submit()
  }, [])

  const searchInputCallbackRef = useCallback(
    (node: SearchInputControl | null) => {
      searchInputInternalRef.current = node
      if (isFunction(searchInputRef)) searchInputRef(node)
      // eslint-disable-next-line react-hooks/immutability, react-compiler/react-compiler -- this is a ref
      else if (searchInputRef) searchInputRef.current = node
    },
    [searchInputRef],
  )

  useLayoutEffect(function adjustBackgroundImageHeight() {
    function handleResize() {
      const image = backgroundImageRef.current
      if (!image || !searchContainerRef.current) return

      const header = document.getElementById('header')
      const headerHeight = header ? header.clientHeight : 0
      const searchContainerRect = searchContainerRef.current.getBoundingClientRect()
      const topRelativeToViewport = searchContainerRect.top
      const availableHeight = topRelativeToViewport + searchContainerRect.height / 2 - headerHeight
      image.style.maxHeight = `${Math.max(0, availableHeight)}px`
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const openPhoneShoppingDialog = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isMobile) {
      return
    }
    e.preventDefault()
    setShowPhoneDialog(true)
  }

  const handlePhoneClose = useStableCallback(() => {
    setShowPhoneDialog(false)
    setShouldFocusTrigger(true)
  })

  return (
    <section className={style.Hero} aria-labelledby='main-heading'>
      <Picture
        src={{
          pic: shopJpg,
        }}
        fetchPriority='high'
        className={style.BackgroundImage}
        alt=''
        preload
        ref={backgroundImageRef}
      />
      <div className={style.HeroContent}>
        <h1 id='main-heading' className={style.Heading}>
          <span className={style.HeadingLine}>Accessible Shopping</span>
          <span className={style.HeadingLine}>Billions of products - thousands of stores</span>
        </h1>

        <div
          className={style.SearchContainer}
          ref={searchContainerRef}
          role='search'
          aria-label='Search for products'
        >
          <div className={style.HeroSearchWrapper} ref={searchWrapperRef}>
            <Suspense fallback={<div className={style.SearchFallback} aria-hidden='true' />}>
              <SearchInputWithAutocomplete
                ref={searchInputCallbackRef}
                placeholderText='What to buy? (e.g., headphones, kitchen tools)'
                variant='main'
              />
            </Suspense>
            <PrimaryButton
              size='xl'
              onPress={handleSearchSubmit}
              className={style.HeroSearchButton}
              aria-label='Search for products'
            >
              Search
            </PrimaryButton>
          </div>
        </div>

        <section className={style.CallToActionSection} aria-labelledby='phone-shopping-heading'>
          <h2 id='phone-shopping-heading' className={style.OrText}>
            Now Available: Shop by Phone — No Internet Required
          </h2>

          <div className={style.PhoneNumbersContainer}>
            <a
              ref={node => {
                if (!showPhoneDialog && shouldFocusTrigger) {
                  node?.focus()
                  setShouldFocusTrigger(false)
                }
              }}
              onClick={openPhoneShoppingDialog}
              href={`tel:${phoneInfo.number}`}
              className={clsx(style.PhoneButton)}
              aria-label={`Call ${phoneInfo.displayNumber} to shop by phone in ${phoneInfo.country}`}
            >
              <img src={CallIcon} alt='' className={style.PhoneIcon} />
              <span className={style.PhoneLabel}>
                {phoneInfo.displayNumber}
                <VisuallyHidden> ({phoneInfo.country})</VisuallyHidden>
              </span>
              <img
                src={phoneInfo.image}
                alt={`${phoneInfo.country} flag`}
                className={style.PhoneFlag}
                aria-hidden='false'
              />
            </a>
          </div>

          <p className={style.CallText}>
            More info and international numbers on our{' '}
            <a
              href='https://blog.innosearch.ai/introducing-1-855-shop-gpt-1-855-746-7478-shop-by-phone-experience'
              className={style.BlogLink}
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Visit info page for more international numbers'
            >
              info page
            </a>
            .
          </p>
        </section>
      </div>

      {showPhoneDialog && <Phone onClose={handlePhoneClose} />}
    </section>
  )
})
